import { CheckCheck, MessageSquareX } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { NotificationBell } from "../SVGComponents";
import Tooltip from "./Tooltip";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import {
  getNotification,
  setNotificationSeen,
} from "../redux/slices/walletSlice";
import { log } from "console";

interface NotificationDropdownProps {
  count: number;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  count,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNotification, setExpandedNotification] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { notification, isLoading } = useSelector((state: any) => state.wallet);
  const notifications = notification?.result?.notifications || [];

  const unreadCount = notifications?.filter((n: any) => !n.read)?.length || 0;

  useEffect(() => {
    dispatch(getNotification());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const markAsRead = async (id: string | undefined, type: "single" | "all") => {
    const payload = { noteid: id, type };
    const action = await dispatch(setNotificationSeen(payload));
    if (action?.payload?.success) {
      dispatch(getNotification());
      if (type === "single") setExpandedNotification(null);
      if (type === "all") setIsOpen(false);
    }
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNotification((prev) => (prev === id ? null : id));
  };

  const handleNotificationClick = (note: any) => {
    // Mark as read if not already read
    if (!note.read) {
      markAsRead(note._id, "single");
    }

    // Determine the route based on notification type
    const getNotificationRoute = (type: string) => {
      const typeLower = type.toLowerCase();
      console.log("544646548564654", typeLower)

      if (typeLower.includes('convert')) return '/history';
      if (typeLower.includes('staking')) return '/history';
      if (typeLower.includes('deposit')) return '/history';
      if (typeLower.includes('withdraw')) return '/history';
      if (typeLower.includes('order')) return '/history';
      if (typeLower.includes('kyc')) return '/kyc';
      if (typeLower.includes('support')) return '/support';
      if (typeLower.includes('p2p')) return '/p2p';

      return null;
    };

    const route = getNotificationRoute(note.for);
    if (route) {
      navigate(route);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
      >
        <NotificationBell />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-[-20px] mt-4 w-80 xs:w-72 bg-white dark:bg-black rounded-md shadow-lg z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-themeColor">
              Notifications
            </h3>
            {notifications.length > 0 && unreadCount > 0 && (
              <Tooltip content="Mark all as read" position="left">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(undefined, "all");
                  }}
                  className="text-[12px] text-gray-500 hover:text-red-500"
                >
                  <CheckCheck size={18} />
                </button>
              </Tooltip>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {notifications.map((note: any) => (
                  <div
                    key={note._id}
                    className="bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer"
                    onClick={() => handleNotificationClick(note)}
                  >
                    <div className="flex items-start px-4 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <h4
                              className={`text-sm font-medium truncate ${note.read
                                ? "text-gray-400"
                                : "text-black dark:text-white"
                                }`}
                            >
                              {note?.for}
                            </h4>
                            {!note.read && (
                              <span className="ml-2 inline-block h-2 w-2 rounded-full bg-blue-500"></span>
                            )}
                          </div>
                          <div className="flex items-center">
                            {expandedNotification === note._id && !note.read && (
                              <Tooltip content="Mark as read" position="bottom">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(note._id, "single");
                                  }}
                                  className="text-[12px] text-gray-500 hover:text-red-500 mr-2"
                                >
                                  <MessageSquareX size={20} />
                                </button>
                              </Tooltip>
                            )}
                            <button
                              onClick={(e) => toggleExpand(note._id, e)}
                              className="text-themeColor focus:outline-none"
                            >
                              {expandedNotification === note._id ? (
                                <IoMdArrowDropup className="h-4 w-4" />
                              ) : (
                                <IoMdArrowDropdown className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-300 mt-1">
                          {new Date(note.createdAt).toLocaleString()}
                        </p>
                        {expandedNotification === note._id && (
                          <div className="mt-2">
                            <p className="text-[13px] text-gray-500 dark:text-gray-200 whitespace-pre-wrap">
                              {note?.message}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 text-center">
            <Link
              to="/notification"
              className="text-sm font-medium text-themeColor hover:text-blue-800"
              onClick={() => {
                setIsOpen(false);
                if (unreadCount > 0) {
                  markAsRead(undefined, "all");
                }
              }}
            >
              View all notifications
            </Link>
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;